import { FormEvent, useState } from "react";
import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { AnalyticsEvent, Routes } from "@ctypes";
import { useEntryIntakeStore } from "@entry/store";
import {
  EntryBody,
  EntryBodyStrong,
  EntryCta,
  EntryTitle,
  GlassInput,
} from "@entry/ui";
import { useEntryAnimation } from "@entry/useEntryAnimation";
import { Translations, i18n } from "@i18n";
import { Analytics } from "@services";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

const NAME_PATTERN = /^[a-zA-ZÀ-ÿ][a-zA-ZÀ-ÿ' -]*$/;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 26px;
`;

const Helper = styled(EntryBody)`
  margin-top: 20px;
  text-align: center;
`;

const isValidName = (value: string) => NAME_PATTERN.test(value.trim());

export const NameScreen = () => {
  const navigate = useNavigate();
  const { intake, setIntake } = useEntryIntakeStore();

  const [firstName, setFirstName] = useState(intake.firstName);
  const [lastName, setLastName] = useState(intake.lastName);
  const [touched, setTouched] = useState({ first: false, last: false });

  useEntryPageAnalytics(Routes.EntryName);

  const { contentStyle, formStyle, ctaStyle, animateOut } = useEntryAnimation();
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  const firstValid = isValidName(firstName);
  const lastValid = isValidName(lastName);
  const canContinue = firstValid && lastValid;

  const firstError =
    touched.first && firstName.trim() && !firstValid
      ? i18n.t(Translations.EntryNameInvalid)
      : undefined;
  const lastError =
    touched.last && lastName.trim() && !lastValid
      ? i18n.t(Translations.EntryNameInvalid)
      : undefined;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canContinue) return;

    Analytics.track(AnalyticsEvent.EntryNamePrimaryCTAPressed);
    setIntake({ firstName: firstName.trim(), lastName: lastName.trim() });
    animateOut(() => navigate(Routes.EntryPreferredName));
  };

  return (
    <Screen>
      <ScreenColumn as="form" onSubmit={handleSubmit} noValidate>
        <div style={{ ...contentStyle, marginTop: 0 }}>
          <EntryTitle $size={36}>
            {i18n.t(Translations.EntryNameTitleLine1)}
            <br />
            {i18n.t(Translations.EntryNameTitleLine2)}
          </EntryTitle>
          <EntryBodyStrong style={{ marginTop: 22 }}>
            {i18n.t(Translations.EntryNameHint)}
          </EntryBodyStrong>
        </div>

        <div style={formStyle}>
          <Fields>
            <GlassInput
              id="entry-first-name"
              name="given-name"
              autoComplete="given-name"
              placeholder={i18n.t(Translations.EntryNameFirstPlaceholder)}
              aria-label={i18n.t(Translations.EntryNameFirstPlaceholder)}
              value={firstName}
              autoFocus
              error={firstError}
              onChange={(event) => setFirstName(event.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, first: true }))}
            />
            <GlassInput
              id="entry-last-name"
              name="family-name"
              autoComplete="family-name"
              placeholder={i18n.t(Translations.EntryNameLastPlaceholder)}
              aria-label={i18n.t(Translations.EntryNameLastPlaceholder)}
              value={lastName}
              error={lastError}
              onChange={(event) => setLastName(event.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, last: true }))}
            />
          </Fields>
          <Helper $align="center">{i18n.t(Translations.EntryNameHelper)}</Helper>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <EntryCta
            type="submit"
            $variant="green"
            $disabled={!canContinue}
            disabled={!canContinue}
            aria-disabled={!canContinue}
          >
            {i18n.t(Translations.EntryNamePrimaryCTA)}
          </EntryCta>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
